import {
  type ResourceBindingContext,
  type ResourceBindingDefinition,
  type ResourceContextName,
  resourceBinding,
  resourceBindingByToken,
  resourceBindingText,
} from '@seen/core/resourceBindingRegistry'
import { combineTransactionSteps, getChangedRanges } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { Transaction } from '@tiptap/pm/state'
import type { Mapping } from '@tiptap/pm/transform'

const REGISTRY_VARIABLE_NODE = 'registryVariable'

// Transaction meta asking the plugin to scan the whole document: set on
// create and whenever the editor's contexts change.
export const REGISTRY_VARIABLE_SCAN_META = 'registryVariableScan'

const LEGACY_TOKEN = /{{([^{}\s]+)}}/g

const CONTEXT_LABELS = {
  patient: 'Participant',
  practitioner: 'Practitioner',
  encounter: 'Encounter',
  location: 'Location',
} as const satisfies Record<ResourceContextName, string>

const CONTEXT_NOUNS = {
  patient: 'a participant',
  practitioner: 'a practitioner',
  encounter: 'an encounter',
  location: 'a location',
} as const satisfies Record<ResourceContextName, string>

export interface DocRange {
  from: number
  to: number
}

export type ScanScope = DocRange[] | 'all'

export function registryVariableContextLabel(
  context: ResourceContextName
): string {
  return CONTEXT_LABELS[context]
}

export function registryVariableMissingContextReason(
  context: ResourceContextName
): string {
  return `needs ${CONTEXT_NOUNS[context]}`
}

export function registryVariableSourceLabel(
  context: ResourceContextName
): string {
  return `from the ${CONTEXT_LABELS[context].toLowerCase()} record`
}

export function registryVariableDeferredHint(
  context: ResourceContextName
): string {
  return `Fills in when ${CONTEXT_NOUNS[context]} is known`
}

export function hasBindingContext(
  binding: ResourceBindingDefinition,
  contexts: ResourceBindingContext
): boolean {
  return Boolean(contexts[binding.context])
}

function legacyTokenBinding(
  token: string
): ResourceBindingDefinition | undefined {
  return resourceBinding(token) ?? resourceBindingByToken(token)
}

// Text a binding resolves to in these contexts, or null while its context is
// missing (the node stays deferred).
export function resolveBindingValue(
  id: string | null,
  contexts: ResourceBindingContext
): string | null {
  const binding = resourceBinding(id)
  if (!binding || !hasBindingContext(binding, contexts)) {
    return null
  }
  return resourceBindingText(binding.id, contexts) ?? null
}

export function changedRanges(
  oldDoc: ProseMirrorNode,
  transactions: readonly Transaction[]
): DocRange[] {
  return getChangedRanges(
    combineTransactionSteps(oldDoc, [...transactions])
  ).map(({ newRange }) => newRange)
}

export function mapScope(scope: ScanScope, mapping: Mapping): ScanScope {
  if (scope === 'all') {
    return scope
  }
  return scope.map(({ from, to }) => ({
    from: mapping.map(from, -1),
    to: mapping.map(to, 1),
  }))
}

function forEachInScope(
  doc: ProseMirrorNode,
  scope: ScanScope,
  visit: (node: ProseMirrorNode, pos: number) => void
): void {
  if (scope === 'all') {
    doc.descendants(visit)
    return
  }
  const seen = new Set<number>()
  const size = doc.content.size
  for (const { from, to } of scope) {
    doc.nodesBetween(
      Math.max(0, Math.min(from, size)),
      Math.max(0, Math.min(to, size)),
      (node, pos) => {
        if (!seen.has(pos)) {
          seen.add(pos)
          visit(node, pos)
        }
      }
    )
  }
}

// Replaces `{{token}}` / `{{binding.id}}` text with deferred registryVariable
// nodes. Unknown tokens stay text.
export function convertLegacyTokens(tr: Transaction, scope: ScanScope): void {
  const nodeType = tr.doc.type.schema.nodes[REGISTRY_VARIABLE_NODE]
  if (!nodeType) {
    return
  }
  const replacements = new Map<number, { to: number; node: ProseMirrorNode }>()
  forEachInScope(tr.doc, scope, (node, pos) => {
    if (!node.isText || !node.text?.includes('{{')) {
      return
    }
    for (const match of node.text.matchAll(LEGACY_TOKEN)) {
      const binding = legacyTokenBinding(match[1])
      if (!binding) {
        continue
      }
      const from = pos + match.index
      replacements.set(from, {
        to: from + match[0].length,
        node: nodeType.create({ id: binding.id }, null, node.marks),
      })
    }
  })
  const descending = [...replacements.entries()].sort(([a], [b]) => b - a)
  for (const [from, { to, node }] of descending) {
    tr.replaceWith(from, to, node)
  }
}

// Fills in deferred variables whose context is now present. With `refresh`,
// variables already filled in are resolved again, so an edited record shows
// its current value; one whose context went away keeps its last value.
export function resolveDeferredVariables(
  tr: Transaction,
  contexts: ResourceBindingContext,
  scope: ScanScope,
  resolvedAt: string,
  refresh = false
): void {
  forEachInScope(tr.doc, scope, (node, pos) => {
    if (
      node.type.name !== REGISTRY_VARIABLE_NODE ||
      (node.attrs.value !== null && !refresh)
    ) {
      return
    }
    const value = resolveBindingValue(node.attrs.id, contexts)
    if (value === null || value === node.attrs.value) {
      return
    }
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, value, resolvedAt })
  })
}
